import { Form, Formik, FormikHelpers } from 'formik';
import React from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import { Grid } from '../../app/StyleGuide';
import { PrimaryButton as Button } from '../../components/Buttons';
import ErrorMessage from '../../components/ErrorMessage';
import { AutoFocusInput } from '../../components/FormElements';
import { Container, PageTitle } from '../../components/Layout';
import { SuccessMessage } from '../../components/AlertMessages';
import useQueryParams from '../../hooks/useQueryParams';
import alertService from '../../services/AlertService';
import userService from '../../services/userService';
import windowService from '../../services/windowService';

const PageContainer = styled(Container)`
  margin: ${Grid._6} auto;
`;

const FormContainer = styled(Form)`
  display: flex;
  flex-wrap: wrap;
  gap: ${Grid._4};

  > div {
    flex: 1 1 520px;
  }

  > button {
    flex: 1;
    min-width: 220px;
  }
`;

const EndedMessage = styled(SuccessMessage)`
  max-width: 600px;
  margin: ${Grid._6} auto;
`;

const ActionState = {
  ENDED: 'ended',
} as const;

type ImpersonateFormValues = {
  email: string;
};

type ServiceError = {
  response?: {
    data?: string;
  };
};

function getServiceErrorMessage(error: unknown): string | undefined {
  return (error as ServiceError | undefined)?.response?.data;
}

export default function Impersonate() {
  const { action } = useQueryParams() as { action?: string };

  const handleSubmit = (
    { email }: ImpersonateFormValues,
    { setSubmitting, setFieldError }: FormikHelpers<ImpersonateFormValues>
  ) => {
    const formattedEmail = email
      .trim()
      .toLowerCase()
      .replace(/^mailto:/, '');

    if (!Yup.string().email().isValidSync(formattedEmail)) {
      setFieldError('email', 'Invalid email address');
      setSubmitting(false);
      return;
    }

    const encodedEmail = encodeURIComponent(formattedEmail);

    userService
      .impersonateUser(encodedEmail)
      .then(() => windowService.redirectTo('/dashboard'))
      .catch((error: unknown) => {
        alertService.showError(getServiceErrorMessage(error));
        setSubmitting(false);
      });
  };

  return (
    <PageContainer>
      {action === ActionState.ENDED && <EndedMessage>Your impersonation session has ended</EndedMessage>}

      <PageTitle className="mb-40">
        <h1>Impersonate</h1>
        <p>Enter the email address of the user to impersonate</p>
      </PageTitle>

      <Formik<ImpersonateFormValues>
        initialValues={{ email: '' }}
        validationSchema={Yup.object({
          email: Yup.string().required('An email address is required'),
        })}
        validateOnChange={false}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, isSubmitting, errors }) => (
          <FormContainer>
            <AutoFocusInput
              placeholder="Email Address"
              onChangeHandler={(e: React.ChangeEvent<HTMLInputElement>) => {
                void setFieldValue('email', e.target.value);
              }}
              value={values.email}
              clearInput={() => setFieldValue('email', '')}
              disabled={isSubmitting}
              data-qa-hook="impersonationEmail"
              autoFocus
              selectTextOnFocus={false}
            />
            <Button
              type="submit"
              data-qa-hook="impersonationImpersonate"
              disabled={isSubmitting}
              operating={isSubmitting}
            >
              Impersonate
            </Button>

            {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
          </FormContainer>
        )}
      </Formik>
    </PageContainer>
  );
}
